import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Stack,
  Divider,
  InputAdornment,
  createTheme,
  ThemeProvider,
  useTheme,
  Alert,
} from "@mui/material";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { debounce } from 'lodash';
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../login/AuthContext";
import { toast } from "react-toastify";
import config from "../../config/config";
import GroupIcon from '@mui/icons-material/Group';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonIcon from '@mui/icons-material/Person';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import { useRef } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArchiveIcon from "@mui/icons-material/Archive";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import TableChartIcon from "@mui/icons-material/TableChart"; // Excel ikonu
import ArticleIcon from "@mui/icons-material/Article"; // Word ikonu
import ImageIcon from "@mui/icons-material/Image"; // Resim ikonu
import DownloadIcon from "@mui/icons-material/Download";

const LICENSE_TYPES = {
  YEARLY: "Yıllık",
  DEMO: "Demo"
};

const STATUS_TYPES = {
  PENDING: 1,
  APPROVED: 2,
  GENERATED: 3,
  REJECTED: 6
};

// API Service remains the same
const licenseService = {
  async fetchCustomerLicenses(customerId, token) {
    return axios.get(
      `${config.baseURL}/lisanslar/musteri-lisans/aktif/${customerId}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  },

  async fetchLicenseDetails(licenseId, token) {
    return axios.get(
      `${config.baseURL}/lisanslar/lisanstalep/${licenseId}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  },

  async createLicenseRequest(data, token) {
    return axios.post(
      `${config.baseURL}/lisanslar/lisanstalep`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
  },

  async updateLicenseRequest(licenseId, data, token) {
    return axios.put(
      `${config.baseURL}/lisanslar/lisanstalep/${licenseId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
  },

  async fetchLicenseDocuments(licenseId, token) {
    return axios.get(
      `${config.baseURL}/lisanslar/lisanstalep-dokuman/${licenseId}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  },

  async deleteDocument(documentId, token) {
    return axios.delete(
      `${config.baseURL}/lisanslar/lisanstalep-dokuman/${documentId}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  }
};

const LicenseRequestForm = () => {
  const { id: licenseId, customerId } = useParams();
  const defaultCustomerId = customerId || null;
  const isEditMode = Boolean(licenseId);
  const navigate = useNavigate();
  const { token, userData } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const fileInputRef = useRef(null);
  const [filesToDelete, setFilesToDelete] = useState([]);

  // Dosya yükleme durumları
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);

  // Loading States güncellenmesi
  const [loadingStates, setLoadingStates] = useState({
    licenses: false,
    submission: false,
    documents: false
  });


  // Form States
  const [formData, setFormData] = useState({
    selectedCustomer: "",
    selectedCustomerData: null,
    selectedLicense: "",
    status: STATUS_TYPES.PENDING,
    statusDisabled: STATUS_TYPES.PENDING,
    renewalForm: {
      BaslangicTarihi: "",
      LisansTipi: "",
      Aciklama: "",
      KullaniciSayisi: 1,
    }
  });

  // Other States
  const [existingLicenses, setExistingLicenses] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const [downloadingFiles, setDownloadingFiles] = useState([]);

  const setLoading = (key, value) => {
    setLoadingStates(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetFormFields = () => {
    setFormData(prev => ({
      ...prev,
      renewalForm: {
        BaslangicTarihi: "",
        LisansTipi: "",
        Aciklama: "",
        KullaniciSayisi: 1,
      }
    }));
  };

  // Dosya tipine göre ikon seçme fonksiyonu
const getFileIcon = (type = "") => {
  if (type.includes("pdf")) return <PictureAsPdfIcon color="error" />;
  if (type.includes("zip") || type.includes("rar")) return <ArchiveIcon color="primary" />;
  if (type.includes("xlsx") || type.includes("xls") || type.includes("spreadsheet")) return <TableChartIcon color="success" />; // Excel
  if (type.includes("doc") || type.includes("docs") || type.includes("word")) return <ArticleIcon color="info" />; // Word
  if (type.includes("png") || type.includes("jpg") || type.includes("jpeg") || type.includes("image")) return <ImageIcon color="warning" />; // Resim
  if (type.includes("xml") || type.includes("txt")) return <DescriptionIcon color="info" />; // XML, Text
  return <InsertDriveFileIcon color="disabled" />; // Default
};


  const fetchCustomerLicenses = useCallback(async (customerId) => {
    setLoading('licenses', true);
    try {
      const response = await licenseService.fetchCustomerLicenses(customerId, token);
      setExistingLicenses(response.data);
    } catch (error) {
      toast.error("Lisanslar yüklenirken hata oluştu");
    } finally {
      setLoading('licenses', false);
    }
  }, [token]);


  const fetchLicenseDetails = useCallback(async () => {
    if (!isEditMode || !licenseId) return;

    try {
      const response = await licenseService.fetchLicenseDetails(licenseId, token);

      if (!response.data || !response.data.data) {
       navigate("/404", { replace: true });;
        return;
      }

      const licenseData = response.data.data;

      try {
        const customerResponse = await axios.get(
          `${config.baseURL}/musteriler/${licenseData.KullaniciNumarasi}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (!customerResponse.data) {
         navigate("/404", { replace: true });;
          return;
        }

        const customerData = customerResponse.data;

        setFormData(prev => ({
          ...prev,
          selectedCustomer: licenseData.KullaniciNumarasi,
          selectedCustomerData: customerData,
          selectedLicense: licenseData.LisansId || "",
          status: licenseData.Durum,
          statusDisabled: licenseData.Durum,
          renewalForm: {
            BaslangicTarihi: licenseData.BaslangicTarih.split("T")[0],
            LisansTipi: licenseData.LisansTipi,
            Aciklama: licenseData.Aciklama || "",
            KullaniciSayisi: licenseData.KullaniciSayisi,
          }
        }));

        // Eğer dökümanlar varsa, onları ayarla
        if (licenseData.Dokumanlar && Array.isArray(licenseData.Dokumanlar)) {
          setExistingDocuments(licenseData.Dokumanlar);
        }

        if (licenseData.LisansId) {
          await fetchCustomerLicenses(licenseData.KullaniciNumarasi);
        }

      } catch (error) {
        console.error("Müşteri bilgileri alınamadı:", error);
       navigate("/404", { replace: true });;
      }
    } catch (error) {
      console.error("Lisans bilgileri alınamadı:", error);
      navigate("/404", { replace: true });;
    }
  }, [isEditMode, licenseId, token, fetchCustomerLicenses, navigate]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        if (isEditMode) {
          await fetchLicenseDetails();
        } else if (defaultCustomerId) {
          try {
            const customerResponse = await axios.get(
              `${config.baseURL}/musteriler/${defaultCustomerId}`,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            const customerData = customerResponse.data;

            setFormData(prev => ({
              ...prev,
              selectedCustomer: defaultCustomerId,
              selectedCustomerData: customerData
            }));

            await fetchCustomerLicenses(defaultCustomerId);

          } catch (error) {
            console.error("Müşteri bilgileri alınamadı:", error);
            toast.error("Müşteri bilgileri yüklenemedi");
          }
        }
      } catch (error) {
        console.error("Veri yüklenirken hata:", error);
      }
    };

    initializeData();
  }, [defaultCustomerId, isEditMode, token, fetchLicenseDetails, fetchCustomerLicenses]);

  useEffect(() => {
    const { selectedCustomer } = formData;
    if (selectedCustomer && !isEditMode) {
      fetchCustomerLicenses(selectedCustomer);
    }
  }, [formData.selectedCustomer, isEditMode, fetchCustomerLicenses]);

  const handleLicenseChange = useCallback((event) => {
    const licenseId = event.target.value;
    setFormData(prev => ({
      ...prev,
      selectedLicense: licenseId
    }));

    if (licenseId) {
      const selectedLicenseData = existingLicenses.find(
        license => license.Id === licenseId
      );

      if (selectedLicenseData) {
        const currentEndDate = new Date(selectedLicenseData.BitisTarih);
        const nextDay = new Date(currentEndDate);
        nextDay.setDate(nextDay.getDate() + 1);

        setFormData(prev => ({
          ...prev,
          renewalForm: {
            BaslangicTarihi: currentEndDate.toISOString().split("T")[0],
            LisansTipi: selectedLicenseData.LisansTipi,
            KullaniciSayisi: selectedLicenseData.UserCount,
            Aciklama: selectedLicenseData.Aciklama || "",
          }
        }));
      }
    } else {
      resetFormFields();
    }
  }, [existingLicenses]);

  const handleRenewalFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      renewalForm: {
        ...prev.renewalForm,
        [name]: value
      }
    }));
  }, []);

  const validateForm = () => {
    const errors = {};

    if (!formData.selectedCustomer) {
      errors.customer = "Müşteri seçimi zorunludur";
    }
    if (!formData.selectedLicense) {
      errors.license = "Lisans seçimi zorunludur";
    }
    if (!formData.renewalForm.BaslangicTarihi) {
      errors.startDate = "Başlangıç tarihi zorunludur";
    }
    if (!formData.renewalForm.LisansTipi) {
      errors.licenseType = "Lisans tipi zorunludur";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading('submission', true);

    try {
      const formDataObj = new FormData();

      const requestData = {
        KullaniciNumarasi: formData.selectedCustomer,
        LisansTipi: formData.renewalForm.LisansTipi,
        LisansNo: formData.selectedCustomerData?.MusteriNo,
        BayiKodu: userData.BayiKodu,
        Aciklama: formData.renewalForm.Aciklama || "",
        BaslangicTarih: formData.renewalForm.BaslangicTarihi,
        Durum: formData.status,
        LisansId: formData.selectedLicense,
      };

      // Form verilerini ekle
      formDataObj.append('LisansNo', requestData.LisansNo || '');
      formDataObj.append('KullaniciNumarasi', requestData.KullaniciNumarasi);
      formDataObj.append('BayiKodu', requestData.BayiKodu);
      formDataObj.append('LisansId', requestData.LisansId);
      formDataObj.append('Durum', requestData.Durum);
      formDataObj.append('LisansTipi', requestData.LisansTipi);
      formDataObj.append('Aciklama', requestData.Aciklama);
      formDataObj.append('BaslangicTarih', requestData.BaslangicTarih);

      // Dosya validasyonu
      if (uploadedFiles.length > 0) {
        const invalidFiles = uploadedFiles.filter(file =>
          file.type !== 'application/pdf' &&
          !file.type.includes('pdf')
        );

        if (invalidFiles.length > 0) {
          toast.error("Yalnızca PDF dosyaları yüklenebilir.");
          setLoading('submission', false);
          return;
        }
      }

      // Dosyaları bir kez ekle
      uploadedFiles.forEach((fileItem) => {
        formDataObj.append('customerFiles', fileItem.file);
      });

      let response;
      if (isEditMode) {
        // İstek gönder
        response = await licenseService.updateLicenseRequest(licenseId, formDataObj, token);

        // Form gönderimi başarılı olduktan sonra silme işlemlerini yap
        if (filesToDelete.length > 0) {
          await Promise.all(
            filesToDelete.map(dokumanId =>
              fetch(`${config.baseURL}/lisanslar/delete-file/${dokumanId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` }
              })
            )
          );
        }

        if (response.data) {
          toast.success("Lisans talebi başarıyla güncellendi");
          navigate("/lisanslar");
        }
      } else {
        response = await licenseService.createLicenseRequest(formDataObj, token);
        if (response.data) {
          toast.success("Lisans yenileme talebi başarıyla oluşturuldu");
          navigate("/lisanslar");
        }
      }
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      const msg = data?.message || data?.error || error.message;
      console.error('API Hatası:', error.message);
      console.error('HTTP Status:', status);
      console.error('Sunucu yanıtı:', typeof data === 'object' ? JSON.stringify(data) : data);

      // API'den gelen hata mesajını göster (canlı sunucuda da okunabilir olsun)
      const errorMessage =
        (typeof msg === 'string' && msg) ||
        (status === 401 && 'Oturum süresi doldu. Tekrar giriş yapın.') ||
        (status === 403 && 'Bu işlem için yetkiniz yok.') ||
        (status === 404 && 'İstek yapılan adres bulunamadı.') ||
        (status === 500 && (data?.error || 'Sunucu hatası. Daha sonra tekrar deneyin.')) ||
        (isEditMode ? 'Güncelleme sırasında hata oluştu.' : 'Lisans yenileme talebi oluşturulurken hata oluştu.');

      toast.error(errorMessage);
    } finally {
      setLoading('submission', false);
    }
  };

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR');
  }, []);

  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 3) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      try {
        setIsSearching(true);
        const response = await axios.get(
          `${config.baseURL}/musteriler/search?query=${encodeURIComponent(query)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSearchResults(response.data.data);
      } catch (error) {
        console.error("Arama hatası:", error);
        toast.error("Müşteri araması yapılırken bir hata oluştu");
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [token]
  );

  const handleCustomerChange = (event) => {
    const value = event.target.value;
    if (value === "search") {
      setDialogOpen(true);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      selectedCustomer: customer.Id,
      selectedCustomerData: customer
    }));
    setDialogOpen(false);
    setSearchQuery("");
    fetchCustomerLicenses(customer.Id);
  };

  const MAX_FILE_SIZE_MB = 10;

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    const validFiles = [];
    for (const file of newFiles) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error("Maksimum dosya boyutu 10MB olmalıdır.");
        continue;
      }
      validFiles.push(file);
    }
    if (validFiles.length > 0) {
      setUploadedFiles(prev => [
        ...prev,
        ...validFiles.map(file => ({
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: URL.createObjectURL(file)
        }))
      ]);
    }
    // Dosya seçimi sonrası input'u temizleme
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 7. Dosya silme fonksiyonu (yeni yüklenenler için)
  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => {
      const newFiles = [...prev];
      // Önce URL'i temizle
      if (newFiles[index]?.preview) {
        URL.revokeObjectURL(newFiles[index].preview);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleDownloadFile = async (fileName) => {
    setDownloadingFiles((prev) => [...prev, fileName]);
    const fileUrl = `${config.baseURL}/siparis/download/${fileName}`;
    try {
      const response = await fetch(fileUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Dosya indirilemedi.");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Dosya indirilemedi");
    } finally {
      setDownloadingFiles((prev) => prev.filter((f) => f !== fileName));
    }
  };

  // 8. Var olan dökümanı silme
  const handleRemoveExistingDocument = async (documentId) => {
    try {
      // Dosyayı backend'den silmek yerine, silmek için işaretle
      setFilesToDelete(prev => [...prev, documentId]);

      // Görsel olarak dosyayı işaretle (isDeleted özelliği ekle)
      setExistingDocuments(prevFiles =>
        prevFiles.map((file) =>
          file.Id === documentId ? { ...file, isDeleted: true } : file
        )
      );

      // toast.success("Dosya silme listesine eklendi");
    } catch (error) {
      console.error("Döküman işaretlenemedi:", error);
      toast.error("Döküman işaretlenirken hata oluştu");
    }
  };

  const cssStyles = `
  .file-deleted {
    text-decoration: line-through;
    opacity: 0.6;
    background-color: rgba(211, 211, 211, 0.3);
  }
`;

  // Dosya boyutunu formatlamak için fonksiyon
const formatFileSize = (sizeInBytes) => {
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  } else if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(2)} KB`;
  } else {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
  }
};

  // 10. Dosya yükleme butonunu tetikle
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isDisabled = formData.statusDisabled === STATUS_TYPES.GENERATED;
  const isSubmitting = loadingStates.submission;
  const outerTheme = useTheme();

  const customTheme = (outerTheme) =>
    createTheme({
      palette: {
        ...outerTheme.palette,
        mode: outerTheme.palette.mode,
      },
      typography: {
        ...outerTheme.typography,
      },
      components: {
        MuiTextField: {
          styleOverrides: {
            root: {
              "--TextField-brandBorderColor": outerTheme.palette.mode === 'dark' ? '#424242' : '#D0D0D0',
              "--TextField-brandBorderHoverColor": outerTheme.palette.mode === 'dark' ? '#616161' : '#9E9E9E',
              "--TextField-brandBorderFocusedColor": outerTheme.palette.mode === 'dark' ? '#757575' : '#616161',
              "& label.Mui-focused": {
                color: "var(--TextField-brandBorderFocusedColor)",
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderColor: "var(--TextField-brandBorderColor)",
            },
            root: {
              [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderHoverColor)",
              },
              [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderFocusedColor)",
              },
              "& input": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
              "& textarea": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: {
              "& .MuiSelect-select": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              borderRadius: "8px",
              border: `1px solid ${outerTheme.palette.mode === 'dark' ? '#424242' : '#E0E0E0'}`,
              transition: "none",
            },
          },
        },
      },
    });

  const SectionHeader = ({ icon: Icon, title }) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
      <Icon
        sx={{
          color: outerTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.54)',
          fontSize: '1.25rem'
        }}
      />
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{
          color: outerTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
          textTransform: 'uppercase',
          fontSize: '0.75rem'
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  const styles = {
    container: {
      width: {
        xs: '95%',
        sm: '90%',
        md: '80%',
        lg: '70%'
      },
      margin: 'auto',
      p: 3
    },
    buttonGroup: {
      mt: 3,
      display: 'flex',
      gap: 2,
      justifyContent: 'flex-start'
    },
    fileUploadContainer: {
      mt: 3,
      border: '2px dashed #ccc',
      borderRadius: 2,
      p: 2,
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s',
      '&:hover': {
        borderColor: 'primary.main',
        backgroundColor: 'rgba(25, 118, 210, 0.04)'
      }
    },
    fileList: {
      mt: 2,
      maxHeight: '300px',
      overflow: 'auto'
    },
    fileItem: {
      border: '1px solid #eee',
      borderRadius: 1,
      mb: 1
    },
    // Silinen dosyalar için stil
    fileDeleted: {
      textDecoration: 'line-through',
      opacity: 0.6,
      backgroundColor: 'rgba(211, 211, 211, 0.3)',
    }
  };

  return (
    <ThemeProvider theme={customTheme(outerTheme)}>
      <div className="cari-listesi-container">
        <style>{cssStyles}</style>
        <Box sx={styles.container}>
          <form onSubmit={handleSubmit}>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
              {isEditMode ? "Lisans Talebi Düzenle" : "Lisans Yenileme Talebi"}
            </Typography>

            {isDisabled && (
              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  borderRadius: 1,
                  '& .MuiAlert-icon': {
                    alignItems: 'center'
                  }
                }}
              >
                Onaylanmış belgede düzenleme yapamazsınız!
              </Alert>
            )}

            <Card elevation={0} sx={{ mb: 1.5 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <SectionHeader icon={PersonIcon} title="Müşteri Bilgileri" />
                <Divider sx={{ mb: 2, mt: 0.5 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={Boolean(formErrors.customer)}
                    >
                      <InputLabel>Müşteri Seçin</InputLabel>
                      <Select
                        value={formData.selectedCustomer}
                        onChange={handleCustomerChange}
                        required
                        label="Müşteri Seçin"
                        disabled={isEditMode || customerId || isDisabled}
                      >
                        <MenuItem value="" disabled>Müşteri Seçin</MenuItem>
                        <MenuItem value="search">
                          <em>Müşteri Ara</em>
                        </MenuItem>
                        {formData.selectedCustomerData && (
                          <MenuItem value={formData.selectedCustomer}>
                            {formData.selectedCustomerData.Unvan}
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ mb: 1.5 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <SectionHeader icon={VpnKeyIcon} title="Lisans Bilgileri" />
                <Divider sx={{ mb: 2, mt: 0.5 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={Boolean(formErrors.license)}
                    >
                      <InputLabel>Mevcut Lisans</InputLabel>
                      <Select
                        value={formData.selectedLicense}
                        onChange={handleLicenseChange}
                        required
                        label="Mevcut Lisans"
                        disabled={isEditMode || isDisabled}
                      >
                        <MenuItem value="" disabled>Lisans Seçin</MenuItem>
                        {existingLicenses.map((license) => (
                          <MenuItem key={license.Id} value={license.Id}>
                            {`${license.UserNo} - ${formatDate(license.BaslangicTarih)} - ${formatDate(license.BitisTarih)} - ${license.LisansTipi}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Başlangıç Tarihi"
                      type="date"
                      name="BaslangicTarihi"
                      value={formData.renewalForm.BaslangicTarihi}
                      onChange={handleRenewalFormChange}
                      required
                      error={Boolean(formErrors.startDate)}
                      helperText={formErrors.startDate}
                      InputLabelProps={{ shrink: true }}
                      disabled={isDisabled}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarTodayIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Kullanıcı Sayısı"
                      type="number"
                      name="KullaniciSayisi"
                      value={formData.renewalForm.KullaniciSayisi}
                      onChange={handleRenewalFormChange}
                      required
                      error={Boolean(formErrors.userCount)}
                      helperText={formErrors.userCount}
                      InputProps={{
                        inputProps: { min: 1 },
                        startAdornment: (
                          <InputAdornment position="start">
                            <GroupIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={Boolean(formErrors.licenseType)}
                    >
                      <InputLabel>Lisans Tipi</InputLabel>
                      <Select
                        name="LisansTipi"
                        value={formData.renewalForm.LisansTipi}
                        onChange={handleRenewalFormChange}
                        required
                        label="Lisans Tipi"
                        disabled={isDisabled}
                      >
                        {Object.values(LICENSE_TYPES).map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ mb: 1.5 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <SectionHeader icon={DescriptionIcon} title="Açıklama" />
                <Divider sx={{ mb: 2, mt: 0.5 }} />
                <TextField
                  fullWidth
                  label="Açıklama"
                  name="Aciklama"
                  value={formData.renewalForm.Aciklama}
                  onChange={handleRenewalFormChange}
                  disabled={isDisabled}
                  multiline
                  rows={4}
                  placeholder="Lisans yenileme talebi için açıklama girin"
                  error={Boolean(formErrors.description)}
                  helperText={formErrors.description}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                        <DescriptionIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ mb: 1.5 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <SectionHeader icon={AttachFileIcon} title="Müşteri Dosyası" />
                <Divider sx={{ mb: 2, mt: 0.5 }} />

  <Paper elevation={1} sx={{ p: 2 }}>
    {/* Dosya Yükleme Butonu */}
    <Box display="flex" alignItems="center" gap={2} mb={2}>
      <Button
        variant="contained"
        component="label"
        startIcon={<UploadFileIcon />}
        disabled={isDisabled}
      >
        Dosya Yükle
        <input
          type="file"
          accept="application/pdf" // Sadece PDF dosyalarına izin ver
          multiple
          hidden
          onChange={handleFileChange}
          ref={fileInputRef}
        />
      </Button>
    </Box>

    {/* Yüklenen Dosyalar */}
    <Stack spacing={2}>
      {/* Mevcut Dökümanlar */}
      {isEditMode && existingDocuments.length > 0 ? (
  existingDocuments.map((doc) => (
    <Paper
      key={doc.Id}
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        mb: 1,
        ...(doc.isDeleted ? styles.fileDeleted : {})
      }}
      className={doc.isDeleted ? "file-deleted" : ""}
    >
      <Box display="flex" alignItems="center" gap={2}>
        {getFileIcon(doc.DosyaAdi)}
        <Box>
          <Typography variant="body1">{doc.DosyaAdi}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatFileSize(doc.DosyaBoyutu)}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" gap={1}>
        {/* İndirme Butonu */}
        <IconButton
          color="primary"
          onClick={() => handleDownloadFile(doc.SistemDosyaAdi)}
          disabled={downloadingFiles.includes(doc.SistemDosyaAdi)}
        >
          {downloadingFiles.includes(doc.SistemDosyaAdi) ? (
            <CircularProgress size={20} />
          ) : (
            <DownloadIcon />
          )}
        </IconButton>
        {/* Sil Butonu - Form kilitli değilse göster */}
        {!isDisabled && !doc.isDeleted && (
          <IconButton
            color="error"
            onClick={() => handleRemoveExistingDocument(doc.Id)}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    </Paper>
  ))
) : (
  isEditMode && loadingStates.documents ? (
    <Box display="flex" justifyContent="center" my={2}>
      <CircularProgress size={24} />
    </Box>
  ) : (
    isEditMode && (
      <Typography variant="body2" color="text.secondary">
        Henüz döküman eklenmemiş
      </Typography>
    )
  )
)}

      {/* Yeni Yüklenen Dosyalar */}
      {uploadedFiles.length > 0 ? (
        uploadedFiles.map((file, index) => (
          <Paper
            key={index}
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              mb: 1
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              {getFileIcon(file.type || file.name)}
              <Box>
                <Typography variant="body1">{file.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(file.size)}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              {/* Sil Butonu */}
              <IconButton
                color="error"
                onClick={() => handleRemoveFile(index)}
                disabled={isDisabled}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Paper>
        ))
      ) : (
        !isEditMode && (
          <Typography variant="body2" color="text.secondary">
            Henüz bir dosya seçilmedi.
          </Typography>
        )
      )}
    </Stack>
  </Paper>
              </CardContent>
            </Card>

            <Box sx={styles.buttonGroup}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting || isDisabled}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                    >
                      {isSubmitting
                        ? "İşleniyor..."
                        : isEditMode
                          ? "Güncelle"
                          : "Yenileme Talebi Oluştur"
                      }
                    </Button>

                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      onClick={() => navigate("/lisanslar")}
                      disabled={isSubmitting}
                    >
                      İptal
                    </Button>

                    {isEditMode && (
                      <FormControl
                        variant="outlined"
                        size="small"
                        disabled={isDisabled}
                        sx={{
                          minWidth: 150,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      >
                        <InputLabel>Durum</InputLabel>
                        <Select
                          value={formData.status}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            status: e.target.value
                          }))}
                          label="Durum"
                        >
                          <MenuItem value={STATUS_TYPES.PENDING}>Beklemede</MenuItem>
                          <MenuItem value={STATUS_TYPES.APPROVED}>Onaylandı</MenuItem>
                          {isDisabled && (
                            <MenuItem value={STATUS_TYPES.GENERATED}>Lisans Üretildi</MenuItem>
                          )}
                          <MenuItem value={STATUS_TYPES.REJECTED}>Reddedildi</MenuItem>
                        </Select>
                      </FormControl>
                    )}
            </Box>
          </form>

        {/* Müşteri Arama Dialog'u */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          sx={{ backdropFilter: 'blur(5px)' }}
          PaperProps={{
            sx: {
              minHeight: '600px',
            },
          }}
        >
          <DialogTitle>Müşteri Ara</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Unvan, Yetkili, Vergi No veya Telefon ile ara"
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              margin="normal"
              placeholder="En az 3 karakter giriniz..."
              autoFocus
            />
            <Box sx={{ mt: 2 }}>
              {isSearching ? (
                <Box display="flex" justifyContent="center" my={2}>
                  <CircularProgress />
                </Box>
              ) : searchResults.length > 0 ? (
                searchResults.map((customer) => (
                  <MenuItem
                    key={customer.Id}
                    onClick={() => handleCustomerSelect(customer)}
                    sx={{
                      display: 'block',
                      p: 2,
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Box>
                      <Box fontWeight="bold">{customer.Unvan}</Box>
                      <Box display="flex" gap={2} color="text.secondary" fontSize="0.875rem">
                        <span>{customer.Yetkili}</span>
                        {customer.VergiNo && <span>VN: {customer.VergiNo}</span>}
                        {customer.Tel && <span>{customer.Tel}</span>}
                      </Box>
                    </Box>
                  </MenuItem>
                ))
              ) : searchQuery.length > 1 ? (
                <Box p={2} textAlign="center" color="text.secondary">
                  Sonuç bulunamadı
                </Box>
              ) : (
                <Box p={2} textAlign="center" color="text.secondary">
                  Arama yapmak için en az 3 karakter giriniz
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="primary">
              Kapat
            </Button>
          </DialogActions>
        </Dialog>
        </Box>
      </div>
    </ThemeProvider>
  );
};

export default LicenseRequestForm